import { FC, MouseEvent } from 'react';
import { Checkbox } from '@mui/material';
import clsx from 'clsx';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { BilledEntityItem } from 'src/use-cases/settings/useBilledEntities';

interface EntityItemProps {
  entity: BilledEntityItem;
  depth: number; // 0 for root, 1 for first sub-level, etc.
  onToggle: () => void;
  expanded: boolean;
  onExpandToggle?: () => void;
}

export const EntityItem: FC<EntityItemProps> = ({ entity, depth, onToggle, expanded, onExpandToggle }) => {
  // Check if entity has subentities
  const hasSubEntities = entity.subEntities && entity.subEntities.length > 0;

  // Handle expand/collapse toggle
  const handleExpandToggle = (e: MouseEvent) => {
    e.stopPropagation();
    if (onExpandToggle) {
      onExpandToggle();
    }
  };
  // Determine text size based on level
  const textSize = 'text-14 xl:text-16';

  // Determine status color based on depth and invoice statuses
  const getStatusColor = () => {
    const status = depth === 0 ? 'APPROVED' : 'FLAGGED';
    return entity?.invoiceStatuses?.find((s) => s.status === status) ? 'bg-primary' : 'bg-orange-30';
  };

  // Get the count of flagged invoices or use a default value
  const getFlaggedCount = (): number => {
    const flaggedStatus = entity.invoiceStatuses?.find((s) => s.status === 'FLAGGED');
    return flaggedStatus?.count || 0;
  };

  // Handler for checkbox click
  const handleCheckboxClick = (e: MouseEvent) => {
    // Prevent parent div click
    e.stopPropagation();

    // Execute toggle
    onToggle();
  };

  // Handler for row click
  const handleRowClick = () => {
    onToggle();
  };

  return (
    <div
      className='flex items-center gap-2 p-6 rounded-4 hover:bg-neutral-10 cursor-pointer'
      onClick={handleRowClick}
      role='button'
      tabIndex={0}
      aria-label={`Toggle ${entity.code}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onToggle();
          e.preventDefault();
        }
      }}
    >
      {/* Using div around checkbox to increase click area */}
      <div
        onClick={handleCheckboxClick}
        role='button'
        tabIndex={0}
        aria-label={`Toggle checkbox for ${entity.code}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleCheckboxClick(e as any);
            e.preventDefault();
          }
        }}
      >
        <Checkbox
          checked={entity.selected}
          sx={{
            color: '#DFDFDF',
            '&.Mui-checked': {
              color: '#00A651',
            },
            padding: 0,
          }}
        />
      </div>
      {hasSubEntities && (
        <div
          onClick={handleExpandToggle}
          role='button'
          tabIndex={0}
          className='flex items-center justify-center cursor-pointer hover:bg-neutral-20 rounded-full w-24 h-24 mx-1'
          aria-label='Toggle sub-entities'
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleExpandToggle(e as any);
              e.preventDefault();
            }
          }}
        >
          {expanded ? (
            <KeyboardArrowDownIcon className='text-neutral-40 !text-[28px] xl:!text-30' />
          ) : (
            <KeyboardArrowRightIcon className='text-neutral-40 !text-[28px] xl:!text-30' />
          )}
        </div>
      )}
      {!hasSubEntities && <div className='w-8' />}
      <span
        className={textSize}
        onClick={handleRowClick}
        role='button'
        tabIndex={0}
        aria-label={`Toggle row for ${entity.code}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleRowClick();
            e.preventDefault();
          }
        }}
      >
        {entity.code}
      </span>

      <div className='flex items-center gap-8 ml-auto'>
        {getFlaggedCount() > 0 && (
          <div className='py-4 px-6 bg-neutral-10 rounded-8 text-12 font-bold text-center' style={{ boxShadow: '0 2px 4px 0 rgba(0,0,0,0.05)' }}>
            {getFlaggedCount()}
          </div>
        )}
        <div className={clsx(`w-8 h-8 rounded-full`, getStatusColor())} />
      </div>
    </div>
  );
};
